import {envIsLocal, envIsVM} from '@wandb/common/config';
import * as globals from '@wandb/common/css/globals.styles';
import DOC_URLS from '@wandb/common/util/doc_urls';
import {useBooleanState} from '@wandb/common/util/hooks';
import {getTimeSegmentsInTimeZone} from '@wandb/common/util/time';
import React, {FC, memo, useMemo} from 'react';
import {useLocation} from 'react-router';
import styled, {css} from 'styled-components';

import {useViewer} from '../../../state/viewer/hooks';
import {Viewer} from '../../../state/viewer/types';
import {useIsAdminModeActiveAndIsNotLocalEnv} from '../../../util/admin';
import {openBeamer, useBeamerHasNewPosts} from '../../../util/beamer';
import {isNonPersonalNonAcademicOrg} from '../../../util/pricing';
import {
  community,
  contactSalesDemo,
  courses,
  fullyConnected,
  homeQuickStart,
  marketingSite,
  pricing,
} from '../../../util/urls';
import {removeNullUndefinedOrFalse} from '../../../util/utility';
import BeamerIcon from '../../BeamerIcon';
import BetaVersionModal, {betaVersion} from '../../BetaVersionModal';
import CopyableText from '../../CopyableText';
import {
  IconBlogReport,
  IconDocumentation,
  IconForumChatBubble,
  IconHeadset,
  IconHelpAlt,
  IconLaptopLocalComputer,
  IconLightbulbInfo,
  IconPrivacyOpen,
  IconShareWith,
  IconStar,
  IconVideoPlay,
} from '../../Icon';
import {Tooltip} from '../../Tooltip';
import NavMenu, {NavMenuSection} from './NavMenu';

type NavResourcesProps = {};

const NavResourcesComp: FC<NavResourcesProps> = () => {
  const {
    state: menuOpen,
    setTrue: openMenu,
    setFalse: closeMenu,
  } = useBooleanState(false);
  const {
    state: tooltipOpen,
    setTrue: openTooltip,
    setFalse: closeTooltip,
  } = useBooleanState(false);
  const {
    state: betaModalOpen,
    setTrue: openBetaModal,
    setFalse: closeBetaModal,
  } = useBooleanState(false);

  const sections = useNavResourceMenuSections({openBetaModal});
  const showAdminBanner = useIsAdminModeActiveAndIsNotLocalEnv();

  const beamerHasNewPosts = useBeamerHasNewPosts();

  return (
    <>
      <NavMenu
        open={menuOpen}
        onOpen={openMenu}
        onClose={closeMenu}
        trigger={
          <Wrapper>
            <Tooltip
              position="bottom center"
              open={tooltipOpen && !menuOpen}
              onOpen={openTooltip}
              onClose={closeTooltip}
              trigger={
                <Button active={menuOpen} showAdminBanner={showAdminBanner}>
                  <Icon />
                  {beamerHasNewPosts && (
                    <BeamerAlertPill showAdminBanner={showAdminBanner} />
                  )}
                </Button>
              }>
              Resource & help
            </Tooltip>
          </Wrapper>
        }
        sections={sections}
      />
      {betaModalOpen && <BetaVersionModal onClose={closeBetaModal} />}
    </>
  );
};

const NavResources = memo(NavResourcesComp);

export default NavResources;

const SHOW_CLOUD_RESOURCES = !envIsVM && !envIsLocal;

const SUPPORT_START_TIME = 7;
const SUPPORT_END_TIME = 15;

function viewerIsPaid(viewer: Viewer | undefined): boolean {
  return viewer?.organizations.some(isNonPersonalNonAcademicOrg) ?? false;
}

type UseNavResourceMenuSectionsParams = {
  openBetaModal: () => void;
};

function useNavResourceMenuSections({
  openBetaModal,
}: UseNavResourceMenuSectionsParams): NavMenuSection[] {
  const viewer = useViewer();
  const location = useLocation();

  const betaVersionSection: NavMenuSection | null = useMemo(() => {
    if (!betaVersion) {
      return null;
    }
    return {
      items: [
        {
          icon: <IconLaptopLocalComputer />,
          label: `Beta v. ${betaVersion}`,
          onClick: openBetaModal,
          rightSide: (
            <CopyContainer>
              <CopyableText
                text={''}
                copyText={`https://beta.wandb.ai${location.pathname}?betaVersion=${betaVersion}`}
                toastText="Copied beta link to clipboard."
                icon="link"
              />
            </CopyContainer>
          ),
        },
      ],
    };
  }, [openBetaModal, location.pathname]);

  const resourcesLoggedOut: NavMenuSection[] = useMemo(
    () =>
      removeNullUndefinedOrFalse([
        {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconPrivacyOpen />,
              label: `Company website`,
              to: marketingSite(),
            },
          ]),
        },

        {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconDocumentation />,
              label: `Documentation`,
              to: `${DOC_URLS.root}?utm_source=app-resource-center&utm_medium=app&utm_term=docs`,
            },
            SHOW_CLOUD_RESOURCES && {
              icon: <IconForumChatBubble />,
              label: `Community`,
              to: community(),
            },
            {icon: <IconVideoPlay />, label: `W&B Courses`, to: courses()},
            SHOW_CLOUD_RESOURCES && {
              icon: <IconBlogReport />,
              label: `Blog`,
              to: fullyConnected(),
            },
          ]),
        },

        SHOW_CLOUD_RESOURCES && {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconLaptopLocalComputer />,
              label: `Request a demo`,
              to: contactSalesDemo(),
            },
            {icon: <IconShareWith />, label: `Private teams`, to: pricing()},
          ]),
        },

        betaVersionSection,
      ]),
    [betaVersionSection]
  );

  const isPaidUser = viewerIsPaid(viewer);
  const currentTimePSTSegments = getTimeSegmentsInTimeZone(
    'America/Los_Angeles'
  );

  const isSupportWorkingHours =
    SUPPORT_START_TIME <= currentTimePSTSegments.hour &&
    currentTimePSTSegments.hour < SUPPORT_END_TIME;
  const showZendeskSupport = isPaidUser || isSupportWorkingHours;

  const resourcesLoggedIn: NavMenuSection[] = useMemo(
    () =>
      removeNullUndefinedOrFalse([
        SHOW_CLOUD_RESOURCES && {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconStar />,
              label: `What's new`,
              onClick: openBeamer,
              rightSide: <BeamerIcon />,
            },
          ]),
        },

        {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconLightbulbInfo />,
              label: `Quickstart`,
              to: `${homeQuickStart()}?utm_source=app-resource-center&utm_medium=app&utm_term=quickstart`,
            },
            {
              icon: <IconDocumentation />,
              label: `Documentation`,
              to: DOC_URLS.root,
            },
            SHOW_CLOUD_RESOURCES && {
              icon: <IconForumChatBubble />,
              label: `Community`,
              to: community(),
            },
            showZendeskSupport && {
              icon: <IconHeadset />,
              label: `Contact Support`,
              // Launches Zendesk widget
              onClick: () => (window as any).openWidget?.(),
            },
          ]),
        },

        {
          items: removeNullUndefinedOrFalse([
            {icon: <IconVideoPlay />, label: `W&B Courses`, to: courses()},
            SHOW_CLOUD_RESOURCES && {
              icon: <IconBlogReport />,
              label: `Blog`,
              to: `${fullyConnected()}?utm_source=app-resource-center&utm_medium=app&utm_term=fully-connected`,
            },
          ]),
        },

        betaVersionSection,
      ]),
    [showZendeskSupport, betaVersionSection]
  );

  return viewer == null ? resourcesLoggedOut : resourcesLoggedIn;
}

export const Wrapper = styled.div<{marginRight?: number}>`
  margin-right: ${p => p.marginRight ?? 16}px;
  height: 100%;
  display: flex;
  align-items: center;
`;

export const Button = styled.button<{
  active: boolean;
  showAdminBanner?: boolean;
}>`
  position: relative;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;

  color: ${props => (props.showAdminBanner ? globals.WHITE : globals.GRAY_500)};
  opacity: ${props => (props.showAdminBanner ? 0.5 : 1)};
  &:hover {
    opacity: ${props => (props.showAdminBanner ? 0.8 : 1)};
    color: white;
  }

  background: transparent;
  &:hover {
    color: ${globals.WHITE};
    background: rgba(250, 250, 254, 0.12);
  }
  ${p =>
    p.active &&
    css`
      &&& {
        color: ${globals.WHITE};
        background: rgba(250, 250, 254, 0.16);
      }
    `}

  &:focus-visible {
    outline: 2px solid rgba(0, 150, 173, 0.6);
  }
`;

const Icon = styled(IconHelpAlt)`
  display: block;
  width: 24px;
  height: 24px;
`;

const BeamerAlertPill = styled.div<{
  showAdminBanner?: boolean;
}>`
  width: 14px;
  height: 14px;
  background-color: ${globals.RED};
  border-radius: 50%;
  border: ${props =>
    props.showAdminBanner
      ? `2px solid ${globals.GOLD_DARK3}`
      : `2px solid ${globals.GRAY_900}`};
  position: absolute;
  top: -1px;
  right: -1px;
`;

const CopyContainer = styled.div`
  &&&&& {
    .copyable-text {
      margin-left: 8px;
      padding-left: 4px;
      i.icon.copy-icon {
        opacity: 1;
        font-size: 1em;
      }
    }
  }
`;
